import _extends from "@babel/runtime/helpers/extends";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import React, { useEffect, useState } from 'react';
import moment from 'moment';
import { Autocomplete, Box, TextField, Typography } from '@mui/material';
import { api } from 'src/api';
import { useStore } from 'src/stores';
/** Customers with live properties first, busiest first; the rest are left out of the list. */
export const activeCompanies = function () {
  let items = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : [];
  return items.filter(c => {
    var _c$activeProperties;
    return c.companyId && c.companyName && ((_c$activeProperties = c.activeProperties) !== null && _c$activeProperties !== void 0 ? _c$activeProperties : 0) > 0;
  }).sort((a, b) => {
    var _b$activeProperties, _a$activeProperties;
    return ((_b$activeProperties = b.activeProperties) !== null && _b$activeProperties !== void 0 ? _b$activeProperties : 0) - ((_a$activeProperties = a.activeProperties) !== null && _a$activeProperties !== void 0 ? _a$activeProperties : 0) || a.companyName.localeCompare(b.companyName);
  });
};

/**
 * Picks the customer for Support: Residents without leaving Livy. Switching runs the same steps as the
 * company switcher (CompaniesSelector.handleCompanySelect), so the header, the token and every page agree on it;
 * the server enforces what the switched token may read.
 */
export function LivyCompanyPicker(_ref) {
  let companyLabel = _ref.companyLabel;
  const store = useStore();
  const _useState = useState(''),
    _useState2 = _slicedToArray(_useState, 2),
    query = _useState2[0],
    setQuery = _useState2[1];
  const _useState3 = useState([]),
    _useState4 = _slicedToArray(_useState3, 2),
    options = _useState4[0],
    setOptions = _useState4[1];
  const _useState5 = useState(false),
    _useState6 = _slicedToArray(_useState5, 2),
    loading = _useState6[0],
    setLoading = _useState6[1];
  const _useState7 = useState(false),
    _useState8 = _slicedToArray(_useState7, 2),
    switching = _useState8[0],
    setSwitching = _useState8[1];
  const _useState9 = useState(null),
    _useState0 = _slicedToArray(_useState9, 2),
    error = _useState0[0],
    setError = _useState0[1];
  useEffect(() => {
    let live = true;
    const timer = setTimeout(async () => {
      setLoading(true);
      try {
        var _res$items;
        const res = await api.AdminApi.getCompaniesList({
          page: 1,
          pageSize: 50,
          search: query
        }, {});
        if (!live) return;
        // The switch reads the company's access data from this list, as the switcher does.
        store.homeDashboardStore.parseCompanyAdminList((_res$items = res === null || res === void 0 ? void 0 : res.items) !== null && _res$items !== void 0 ? _res$items : []);
        setOptions(activeCompanies(res === null || res === void 0 ? void 0 : res.items));
      } catch (_unused) {
        if (live) setOptions([]);
      } finally {
        if (live) setLoading(false);
      }
    }, 250);
    return () => {
      live = false;
      clearTimeout(timer);
    };
  }, [query, store]);
  const handleSwitch = async company => {
    if (!company || company.companyId === store.homeDashboardStore.companyAccessId) return;
    setSwitching(true);
    setError(null);
    try {
      var _access;
      await store.admin.getAdminAccountLoginImpersonateAsync.run({
        companyId: company.companyId
      }, [company.companyId]);
      let access = company.companyAccessData;
      if (!company.companyHasAccessToken || moment((_access = access) === null || _access === void 0 ? void 0 : _access.expiresOn).utc().valueOf() < moment.utc().valueOf()) {
        const r = await store.account.refreshAccessTokenAsync.run(null, [company.companyId]);
        access = r !== null && r !== void 0 && r.isSuccessful ? r.response : undefined;
      } else {
        await store.account.refreshAccessTokenAsync.run(access, [company.companyId]);
      }
      await store.homeDashboardStore.setCompanyAccessId(company.companyId, access);
      store.admin.saveSelectedCompanyName(company.companyName);
    } catch (_unused2) {
      setError("Could not switch to ".concat(company.companyName, ". Try again."));
    } finally {
      setSwitching(false);
    }
  };
  return /*#__PURE__*/React.createElement(Box, {
    sx: {
      width: '100%',
      maxWidth: 420
    }
  }, /*#__PURE__*/React.createElement(Autocomplete, {
    options: options,
    loading: loading || switching,
    disabled: switching,
    filterOptions: x => x,
    getOptionLabel: o => o.companyName,
    isOptionEqualToValue: (a, b) => a.companyId === b.companyId,
    onInputChange: (_, v, reason) => reason === 'input' && setQuery(v),
    value: null,
    blurOnSelect: true,
    onChange: (_, v) => void handleSwitch(v),
    noOptionsText: loading ? 'Loading customers…' : 'No active customer matched.',
    renderOption: (props, o) => /*#__PURE__*/React.createElement("li", _extends({}, props, {
      key: o.companyId
    }), /*#__PURE__*/React.createElement(Box, null, /*#__PURE__*/React.createElement(Typography, {
      variant: "body2"
    }, o.companyName), /*#__PURE__*/React.createElement(Typography, {
      variant: "caption",
      color: "text.secondary"
    }, o.activeProperties, " active ", o.activeProperties === 1 ? 'property' : 'properties'))),
    renderInput: params => /*#__PURE__*/React.createElement(TextField, _extends({}, params, {
      size: "small",
      label: "Customer",
      placeholder: companyLabel ? "".concat(companyLabel, " (change)") : 'Choose a customer',
      error: !!error,
      helperText: error !== null && error !== void 0 ? error : undefined
    }))
  }));
}